import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Panel } from "@/components/ui-bits";
import { CATEGORY_LABEL, INR } from "@/lib/constants";
import { CheckCircle2, AlertTriangle, IndianRupee, Sparkles } from "lucide-react";
import { toast } from "sonner";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function RenewDialog({ open, onOpenChange, onSuccess }) {
  const qc = useQueryClient();
  const [busyPlan, setBusyPlan] = useState(null);

  const { data: plans = [] } = useQuery({
    queryKey: ["plans"],
    queryFn: async () => (await apiClient.get("/plans")).data,
    enabled: open,
  });
  const { data: cfg } = useQuery({
    queryKey: ["payments-config"],
    queryFn: async () => (await apiClient.get("/payments/config")).data,
    enabled: open,
  });

  const startCheckout = async (plan) => {
    setBusyPlan(plan.id);
    try {
      const { data: order } = await apiClient.post("/payments/create-order", { plan_id: plan.id });
      if (order.mocked) {
        // Mock flow — pretend payment succeeds, verify with fake signature
        await new Promise((r) => setTimeout(r, 800));
        const fakePaymentId = `pay_mock_${Math.random().toString(36).slice(2, 12)}`;
        await apiClient.post("/payments/verify", {
          razorpay_order_id: order.order_id,
          razorpay_payment_id: fakePaymentId,
          razorpay_signature: "mock-signature",
        });
        toast.success(`Paid ${INR(plan.price_inr)} (TEST/MOCK) — plan activated`);
        qc.invalidateQueries({ queryKey: ["customer-me"] });
        qc.invalidateQueries({ queryKey: ["payments-history"] });
        onSuccess?.();
        onOpenChange(false);
        setBusyPlan(null);
        return;
      }
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Razorpay checkout script failed to load");
      const rzp = new window.Razorpay({
        key: order.key_id,
        order_id: order.order_id,
        amount: order.amount,
        currency: order.currency,
        name: "NammaXFit",
        description: plan.name,
        prefill: order.prefill,
        theme: { color: "#CCFF00" },
        handler: async (resp) => {
          try {
            await apiClient.post("/payments/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment verified — plan activated");
            qc.invalidateQueries({ queryKey: ["customer-me"] });
            qc.invalidateQueries({ queryKey: ["payments-history"] });
            onSuccess?.();
            onOpenChange(false);
          } catch (e) {
            toast.error("Payment verification failed");
          }
          setBusyPlan(null);
        },
        modal: { ondismiss: () => setBusyPlan(null) },
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusyPlan(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-zinc-800 text-white max-w-3xl" data-testid="renew-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl uppercase tracking-tight">Choose your plan</DialogTitle>
        </DialogHeader>
        {cfg?.mocked && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 text-xs uppercase tracking-[0.16em] px-3 py-2 rounded-sm flex items-center gap-2">
            <AlertTriangle size={14} /> Razorpay is in MOCK mode — purchases use a test simulation. Add live keys to enable real checkout.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto scrollbar-thin">
          {plans.map((p) => (
            <div key={p.id} data-testid={`plan-${p.id}`} className="bg-zinc-950 border border-zinc-900 hover:border-volt p-4 rounded-sm flex flex-col">
              <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">{CATEGORY_LABEL[p.category] || p.category}</div>
              <div className="font-display text-2xl font-black uppercase leading-tight mt-1">{p.name}</div>
              <div className="font-display text-4xl font-black text-volt mt-3">
                <IndianRupee size={22} className="inline -mt-1" />{p.price_inr.toLocaleString("en-IN")}
              </div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mt-1">{p.duration_days} days{p.total_sessions ? ` · ${p.total_sessions} sessions` : ""}</div>
              <div className="text-xs text-zinc-400 mt-3 flex-1">{p.description}</div>
              <Button
                onClick={() => startCheckout(p)}
                disabled={busyPlan === p.id}
                data-testid={`buy-plan-${p.id}`}
                className="mt-4 bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm">
                {busyPlan === p.id ? "Opening…" : <><CheckCircle2 size={14} className="mr-1" /> Buy / Renew</>}
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
